<template lang="pug">
  .col.vuetable-wrapper
    .loader.text-center(v-if="loading")
      i.fas.fa-spinner.fa-spin.fa-2x
    vuetable.vuetable.w-100(ref="usersVuetable"
                        v-show="!loading"
                        :api-url="tableApiUrl"
                        :http-options="httpOptions"
                        :http-fetch="axiosFetch"
                        :css="css.table"
                        :fields="fields"
                        :sort-order="defaultSortOrder"
                        @vuetable:pagination-data="onPaginationData"
                        @vuetable:load-success="onLoadSuccess"
                        @vuetable:loading="loading = true"
                        @vuetable:loaded="loading = false")
      template(slot="username", slot-scope="props")
        | {{ props.rowData.first_name }} {{ props.rowData.last_name }}

      template(slot="organization", slot-scope="props")
        | {{ props.rowData.organization.name }}

      template(slot="last_login_time", slot-scope="props")
        | {{ props.rowData.last_login_at }}

      template(slot="authority", slot-scope="props")
        span(v-if="props.rowData.read_only") {{ $t('user.read_only') }}
        span(v-else) {{ $t('user.read_and_write') }}

      template(slot="actions", slot-scope="props")
        button.mr-3.user-actions(@click="openLoginHistoryModal(props.rowData)")
          i.fas.fa-history.icon-action
        button.mr-3.user-actions(@click='setUser(props.rowData)')
          i.far.fa-edit.icon-action
        button.mr-3.user-actions(
          @click="openModalDeleteUser(props.rowData)"
          v-b-modal.modal-delete-user
        )
          i.far.fa-trash-alt.icon-action

    pagination-wrapper(
      v-show="!loading"
      :pagination="pagination"
    )
      vuetable-pagination(ref="usersPagination"
                          :css="css.pagination"
                          @vuetable-pagination:change-page="onChangePage")
    delete-user-modal(
      :name="userForDelete.name"
      @cancelDelete="cancelDeleteUser"
      @acceptDelete="acceptDeleteUser"
    )

    b-modal(
      id="modal-login-history"
      size="lg"
      hide-header
      no-close-on-backdrop
      hide-footer
      )
        login-history(
          v-if="loginHistory.show"
          :id="loginHistory.id"
          :name="loginHistory.name"
          @closeModal="closeLoginHistoryModal"
        )
</template>

<script>
  import { api } from '@/api.js'
  import Vuetable from 'vuetable-2/src/components/Vuetable'
  import VuetablePagination from 'vuetable-2/src/components/VuetablePagination'
  import PaginationWrapper from '@/components/shared/pagination-wrapper'
  import { getApiPath } from '@/helpers/api.js'
  import DeleteUserModal from '@/pages/Admin/User/List/delete-user-modal'
  import LoginHistory from '@/pages/Admin/User/List/login-history'

  export default {
    components: {
      Vuetable,
      VuetablePagination,
      DeleteUserModal,
      PaginationWrapper,
      LoginHistory
    },

    data() {
      return {
        loading: true,
        userForDelete: {
          id: null,
          name: ''
        },
        pagination: {
          total: 0,
          per_page: 30,
          current_page: 1,
          last_page: 1
        },
        css: {
          table: {
            ascendingIcon: 'fas fa-chevron-up',
            descendingIcon: 'fas fa-chevron-down'
          },
          pagination: {
            wrapperClass: 'vuetable-pagination',
            activeClass: 'btn pagination-button active',
            disabledClass: 'disabled',
            pageClass: 'btn pagination-button',
            linkClass: 'btn pagination-button',
            icons: {
              first: 'fas fa-angle-double-left',
              prev: 'fas fa-angle-left',
              next: 'fas fa-angle-right',
              last: 'fas fa-angle-double-right'
            }
          }
        },
        tableApiUrl: getApiPath('/users'),
        httpOptions: {
          headers: api.defaults.headers.common
        },
        defaultSortOrder: [
          {
            field: 'id',
            direction: 'asc'
          },
        ],
        fields: [
          {
            name: 'id',
            title: () => this.$t('users_list.no'),
            sortField: 'id',
            titleClass: 'no-column',
            dataClass: 'no-column'
          },
          {
            name: 'email' ,
            title: () => this.$t('users_list.account_id'),
            sortField: 'email',
            titleClass: 'email-column',
            dataClass: 'email-column'
          },
          {
            name: '__slot:username' ,
            title: () => this.$t('users_list.username'),
            sortField: 'first_name, last_name',
            titleClass: 'username-column',
            dataClass: 'username-column'
          },
          {
            name: '__slot:organization',
            title: () => this.$t('users_list.organization'),
            sortField: 'organization',
            titleClass: 'organization-column',
            dataClass: 'organization-column'
          },
          {
            name: 'department',
            title: () => this.$t('users_list.department'),
            sortField: 'department',
            titleClass: 'department-column',
            dataClass: 'department-column'
          },
          {
            name: '__slot:authority',
            title: () => this.$t('users_list.authority'),
            sortField: 'read_only',
            titleClass: 'authority-column',
            dataClass: 'authority-column'
          },
          {
            name: '__slot:last_login_time',
            title: () => this.$t('users_list.last_login_time'),
            sortField: 'last_login_at',
            titleClass: 'last-login-time-column',
            dataClass: 'last-login-time-column'
          },
          {
            name: '__slot:actions',
            dataClass: 'actions-column'
          }
        ],
        loginHistory: {
          show: false,
          id: null,
          name: ''
        }
      }
    },

    computed: {
      deleteUserUrl() {
        return `users/${this.userForDelete.id}`
      }
    },

    mounted() {
      this.$root.$on('bv::modal::hide', () => {
        this.loginHistory.show = false
        this.loginHistory.id = null
        this.loginHistory.name = ''
      })
    },

    methods: {
      axiosFetch(apiUrl, httpOptions) {
        return api.get(apiUrl, httpOptions)
      },
      onPaginationData(paginationData) {
        this.$refs.usersPagination.setPaginationData(paginationData)
      },

      onChangePage(page) {
        this.$refs.usersVuetable.changePage(page)
      },

      onLoadSuccess(response) {
        this.pagination.current_page = Number(response.headers['page'])
        this.pagination.per_page = Number(response.headers['per-page'])
        this.pagination.total = Number(response.headers['total'])
        this.pagination.last_page = Math.ceil(
          response.headers['total'] / response.headers['per-page']
        )
      },

      transform(data) {
        return {
          links: {
            pagination: this.pagination
          },
          data: data.data
        }
      },

      openModalDeleteUser({ id, first_name, last_name}) {
        const name = `${first_name} ${last_name}`
        this.userForDelete = { ...this.userForDelete, id, name }
      },

      cancelDeleteUser() {
        this.userForDelete.id = null
        this.userForDelete.name = null
      },

      acceptDeleteUser() {
        api.delete(this.deleteUserUrl).then((resp) => {
          if (resp.data.success) {
            this.$refs.usersVuetable.refresh()
          }
        }).catch(() => {
          this.$bvToast.show('delete-request-error')
        })
      },

      setUser(user) {
        this.$emit('selectUser', user)
      },

      openLoginHistoryModal({ id, first_name, last_name}) {
        this.loginHistory.show = true
        this.loginHistory.id = id
        this.loginHistory.name = `${first_name} ${last_name}`
        this.$bvModal.show('modal-login-history')
      },

      closeLoginHistoryModal() {
        this.$bvModal.hide('modal-login-history')
      }
    }
  }
</script>

<style lang="scss" scoped>
  ::v-deep .vuetable {
    min-width: 1000px !important;

    th {
      color: #7c91a7;
      font-size: 12px;
      padding: 0 10px 20px;
      white-space: nowrap;

      &:hover {
        color: #009cea !important;
      }

      &:first-of-type {
        padding-left: 0;
      }

      .sort-icon {
        float: none !important;
        font-family: "Font Awesome 5 Free", sans-serif;
        font-size: 10px;
        font-style: normal;
        font-variant: normal;
        font-weight: 900;
        line-height: 1;
        margin-left: 2px;
        text-rendering: auto;

        &::before {
          content: "\f078";
        }

        &.fa-chevron-up::before {
          content: "\f077";
        }
      }

      &.no-column {
        width: 5%;
      }

      &.email-column {
        width: 15%;
      }

      &.username-column {
        width: 17%;
      }

      &.organization-column {
        width: 22%;
      }

      &.department-column {
        width: 8%;
      }

      &.authority-column {
        width: 9%;
      }

      &.last-login-time-column {
        width: 17%;
      }

      &.actions-column {
        width: 8%;
      }
    }

    tbody {
      tr {
        border-bottom: 1px solid #e7ecf4;

        &:hover {
          background: #fff;
          border-bottom: 1px solid transparent;
          box-shadow: 0 2px 20px 0 rgba(176, 186, 197, 0.2);
        }
      }

      td {
        color: #313541;
        font-size: 14px;
        padding: 20px 10px;

        &:first-of-type {
          padding-left: 0;
        }

        &:last-of-type {
          padding-right: 15px;
        }

        &.no-column {
          width: 5%;
        }

        &.email-column {
          width: 15%;
        }

        &.username-column {
          width: 17%;
        }

        &.organization-column {
          width: 22%;
        }

        &.department-column {
          width: 8%;
        }

        &.authority-column {
          width: 9%;
        }

        &.last-login-time-column {
          width: 20%;
        }

        &.actions-column {
          display: flex;
          width: 5%;

          .user-actions {
            background-color: transparent;
            border: 0;
            margin: 0;
            padding: 2%;
          }
        }
      }

      .property-link {
        color: #313541;
        text-decoration: none;

        &:hover {
          color: #009cea;
        }
      }

      .icon-action {
        color: #7c91a7;
        font-size: 16px;

        &:hover {
          color: #009cea;
        }
      }

      .dropdown-toggle,
      .dropdown-toggle:focus,
      .dropdown-toggle:active {
        background: transparent !important;
        border: none;
        box-shadow: none;
        color: #7c91a7 !important;
        padding: 0;

        &::after {
          display: none;
        }
      }

      .dropdown-toggle:hover {
        background: transparent !important;
        border: none;
        box-shadow: none;
        color: #009cea !important;
      }

      .data-name-column,
      .data-address-column {
        max-width: 170px;
        word-wrap: break-word;
      }
    }
  }

  ::v-deep .vuetable-pagination {
    text-align: center;

    a {
      cursor: pointer;
      font-size: 14px;
    }
  }
</style>
